import ar from './ar';
import bg from './bg';
import cs from './cs';
import da from './da';
import de from './de';
import el from './el';
import en from './en';
import es from './es';
import esMX from './es-MX';
import fi from './fi';
import fr from './fr';
import hr from './hr';
import hu from './hu';
import it from './it';
import ja from './ja';
import ko from './ko';
import nl from './nl';
import no from './no';
import pl from './pl';
import pt from './pt';
import ro from './ro';
import ru from './ru';
import sk from './sk';
import sl from './sl';
import sr from './sr';
import sv from './sv';
import th from './th';
import tr from './tr';
import uk from './uk';
import vi from './vi';
import zh from './zh';
import zhTW from './zh-TW';
import { isTrackingConsentCategoryKey } from './types';

import type {
  ResolvedTrackingConsentStrings,
  TrackingConsentBanner,
  TrackingConsentCopyKey,
  TrackingConsentTrackerGroup
} from '../types';
import type { TrackingConsentCategoryLabels, TrackingConsentLocale } from './types';

export const TRACKING_CONSENT_CATEGORY_KEYS = ['strictly_necessary', 'analytics', 'marketing'] as const;

const ORGANIZER_COPY_KEYS: readonly Exclude<
  TrackingConsentCopyKey,
  'save_error' | 'close_label' | 'allow_all_optional_categories_label'
>[] = [
  'title',
  'description',
  'allow_label',
  'reject_label',
  'settings_label',
  'policy_link_label',
  'categories_label',
  'always_on_label',
  'required_label',
  'save_preferences_label'
];

export const ENGLISH_DEFAULT_COPY = en.copy;

export const TRACKING_CONSENT_LOCALES: Readonly<Record<string, TrackingConsentLocale>> = {
  ar,
  bg,
  cs,
  da,
  de,
  el,
  en,
  es,
  'es-MX': esMX,
  fi,
  fr,
  hr,
  hu,
  it,
  ja,
  ko,
  nl,
  no,
  pl,
  pt,
  ro,
  ru,
  sk,
  sl,
  sr,
  sv,
  th,
  tr,
  uk,
  vi,
  zh,
  'zh-TW': zhTW
};

const normalizeLanguage = (language: string | null | undefined): string =>
  language && TRACKING_CONSENT_LOCALES[language] ? language : 'en';

const pickOrganizerOverrides = (
  banner: TrackingConsentBanner
): Partial<Required<Pick<TrackingConsentBanner, TrackingConsentCopyKey>>> =>
  Object.fromEntries(
    ORGANIZER_COPY_KEYS.flatMap(key => {
      const value = banner[key];
      return typeof value === 'string' && value.trim() ? [[key, value]] : [];
    })
  );

const resolveTrackerGroup = (
  labels: TrackingConsentCategoryLabels,
  group: TrackingConsentTrackerGroup
): TrackingConsentTrackerGroup => {
  if (!isTrackingConsentCategoryKey(group.key)) {
    return group;
  }
  const translation = labels[group.key];
  const english = en.categories[group.key];
  const englishName = english.name;
  const englishPurpose = english.purpose;
  const localizedName = translation.name;
  const localizedPurpose = translation.purpose;
  return {
    ...group,
    name: group.name === englishName ? localizedName : group.name,
    purpose: englishPurpose && group.purpose === englishPurpose ? (localizedPurpose ?? group.purpose) : group.purpose
  };
};

export const resolveTrackingConsentStrings = (
  language: string | null | undefined,
  banner: TrackingConsentBanner,
  trackerGroups: readonly TrackingConsentTrackerGroup[]
): ResolvedTrackingConsentStrings => {
  const locale = TRACKING_CONSENT_LOCALES[normalizeLanguage(language)] ?? en;
  return {
    ...locale.copy,
    ...pickOrganizerOverrides(banner),
    tracker_groups: trackerGroups.map(group => resolveTrackerGroup(locale.categories, group))
  };
};

export type { TrackingConsentCategoryLabels, TrackingConsentLocale } from './types';
